import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Drawer,
  IconButton,
} from "@mui/material";
import { ArrowBack, Check, Close, ExpandMore, InfoOutlined, ListAlt, LockOpen, Print, Save, TableChart } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import SavingOverlay from "../components/SavingOverlay";
import ConfirmModal from "../components/modals/ConfirmModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useNavigation, useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import {
  patientAssessmentsService,
  patientAssessmentErrorMessage,
  AssessmentApplicationFill,
  FillQuestion,
  FillResult,
  FillSection,
} from "../services/patientAssessmentsService";

// Página-filha (F2 — RN-537): preenchimento de uma aplicação de avaliação (?applicationId=).
// Autosave por resposta no servidor (rascunho retomável de qualquer máquina); conclusão pede
// o parecer do profissional e mostra os resultados calculados junto das instruções de
// interpretação do formulário e das seções. Reabertura permitida em até 15 dias.

interface LocalAnswer {
  optionIds: string[];
  textValue: string;
  comment: string;
}

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

/** Momento atual no formato ISO sem fuso, para casar com as datas que a API devolve. */
const localIsoNow = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const PARECER_MAX = 4000;
const TEXT_MAX = 4000;
const COMMENT_MAX = 2000;

const GuideBox: React.FC<{ text: string }> = ({ text }) => (
  <Box className="c4u-info-box">
    <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
    <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary, whiteSpace: "pre-wrap" }}>
      {text}
    </Typography>
  </Box>
);

/** Um marco da trilha: "Solicitada em 31/08/2026 14:20 por Fulano". */
const Milestone: React.FC<{ label: string; at: string | null; by: string | null; pending?: string }> = ({
  label, at, by, pending,
}) => (
  <Typography sx={{ fontSize: "0.78rem", color: colors.textSecondary }}>
    <strong>{label}:</strong>{" "}
    {at ? `${formatIsoDate(at)} ${timeOf(at)}${by ? ` por ${by}` : ""}` : pending ?? "—"}
  </Typography>
);

const ResultCard: React.FC<{ result: FillResult }> = ({ result }) => {
  return (
    <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", p: 1.5, backgroundColor: "#fff", flex: "1 1 220px", minWidth: 200 }}>
      <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 0.5 }}>
        {result.sectionTitle ?? "Resultado geral"} — {result.calcTypeName}
      </Typography>
      {result.value !== null && (
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 700, color: colors.primary }}>
          {result.value}
        </Typography>
      )}
      {result.breakdown.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {result.breakdown.map(item => (
            <Typography key={item.label} sx={{ fontSize: "0.85rem", color: colors.text }}>
              {item.label}: <strong>{item.value}</strong>
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};


/** Um grupo do índice: a seção, quantos já foram respondidos e os itens para saltar. */
interface NavGroup {
  id: string;
  title: string;
  level: number;
  /** itens DIRETAS da seção (a mãe costuma não ter nenhuma) */
  questions: { id: string; number: number; text: string; answered: boolean; required: boolean; incomplete: boolean }[];
  /** contagem da seção INCLUINDO as filhas — sem isso a mãe do Portage mostraria "0 de 0" */
  answeredTotal: number;
  questionsTotal: number;
  /** texto próprio no lugar do contador (usado pela conclusão, que não tem itens) */
  hint?: string;
  /** conclusão bloqueada enquanto faltam obrigatórias */
  disabled?: boolean;
}

const goTo = (elementId: string) => {
  document.getElementById(elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Índice navegável (RN-537f): em formulários longos — o Portage tem 535 itens — rolar até
    o meio é inviável. Mostra o quanto de cada seção já foi respondido e salta para o item. */
const QuestionNav: React.FC<{
  groups: NavGroup[];
  onSelect?: (groupId: string) => void;
  onNavigate?: () => void;
}> = ({ groups, onSelect, onNavigate }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {groups.map(group => {
        const answered = group.answeredTotal;
        const total = group.questionsTotal;
        const complete = total > 0 && answered === total;
        const hasIncomplete = group.questions.some(q => q.incomplete);
        const expandable = group.questions.length > 0;
        const expanded = expandable && (open[group.id] ?? false);
        return (
          <Box key={group.id}>
            <Box
              onClick={() => {
                if (group.disabled) return;
                setOpen(prev => ({ ...prev, [group.id]: !expanded }));
                onSelect?.(group.id);
                setTimeout(() => goTo(`section-${group.id}`), 0);
                onNavigate?.();
              }}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                cursor: group.disabled ? "not-allowed" : "pointer",
                opacity: group.disabled ? 0.55 : 1,
                p: 0.75, borderRadius: "6px", pl: group.level === 2 ? 2 : 0.75,
                "&:hover": { backgroundColor: group.disabled ? "transparent" : "#e0f5f7" },
              }}
            >
              {/* vermelho = respondido com pendência; laranja = em andamento; cinza = intocado */}
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: hasIncomplete ? "#dc3545" : complete ? "#28a745" : answered > 0 ? "#ff9800" : "#dee2e6" }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: group.level === 1 ? 600 : 500, color: colors.text, lineHeight: 1.3 }}>
                  {group.title}
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: colors.textSecondary }}>
                  {group.hint ?? `${answered} de ${total} respondidos`}
                </Typography>
              </Box>
              {expandable && (
                <ExpandMore sx={{ fontSize: "1rem", color: colors.textSecondary, transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
              )}
            </Box>
            {expanded && group.questions.map(q => (
              <Box
                key={q.id}
                onClick={() => {
                  onSelect?.(group.id);
                  setTimeout(() => goTo(`question-${q.id}`), 0);
                  onNavigate?.();
                }}
                sx={{
                  display: "flex", alignItems: "flex-start", gap: 0.75, cursor: "pointer",
                  py: 0.4, pl: group.level === 2 ? 3.5 : 2.25, pr: 0.75, borderRadius: "6px",
                  "&:hover": { backgroundColor: "#e0f5f7" },
                }}
              >
                <Box sx={{ width: 6, height: 6, borderRadius: "50%", mt: "6px", flexShrink: 0, backgroundColor: q.incomplete ? "#dc3545" : q.answered ? "#28a745" : q.required ? "#ed6c02" : "#dee2e6" }} />
                <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                  {q.number}. {q.text}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })}
    </Box>
  );
};

const AssessmentApplication: React.FC = () => {
  const { getParam, navigateTo } = useRouter();
  const { goToDashboard } = useNavigation();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const applicationId = getParam("applicationId") || "";
  // Prévia do formulário montado (RN-536e): mesma tela, aberta por ?formId=. Os campos
  // funcionam para o usuário experimentar; nada vai para o servidor.
  const previewFormId = getParam("formId") || "";
  const isPreview = previewFormId !== "";
  // Aberta em nova aba pelo cadastro: fechar devolve o foco ao paciente já carregado;
  // acessada direto pela URL, não há aba de origem — aí navega para o cadastro
  const openedInNewTab = typeof window !== "undefined" && window.opener !== null;

  const [fill, setFill] = useState<AssessmentApplicationFill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState<Record<string, LocalAnswer>>({});
  // Trilha de cada resposta (quem editou e quando) — vem do servidor e é atualizada no autosave
  const [answerTrail, setAnswerTrail] = useState<Record<string, { at: string; by: string | null }>>({});
  const [interpretation, setInterpretation] = useState("");
  const [savingCount, setSavingCount] = useState(0);
  const [completing, setCompleting] = useState(false);
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmReopen, setConfirmReopen] = useState(false);
  const [reopening, setReopening] = useState(false);
  const textDebounce = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingAnswers = useRef<Record<string, LocalAnswer>>({});
  const writeQueue = useRef<Record<string, Promise<unknown>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = isPreview
        ? await patientAssessmentsService.getFormPreview(previewFormId)
        : await patientAssessmentsService.getFill(applicationId);
      setFill(data);
      const map: Record<string, LocalAnswer> = {};
      const trail: Record<string, { at: string; by: string | null }> = {};
      data.answers.forEach(a => {
        map[a.questionId] = {
          optionIds: a.optionIds,
          textValue: a.textValue ?? "",
          comment: a.comment ?? "",
        };
        trail[a.questionId] = { at: a.updatedAt, by: a.updatedByName };
      });
      setAnswers(map);
      setAnswerTrail(trail);
      setInterpretation(data.professionalInterpretation ?? "");
    } catch (e) {
      setError(patientAssessmentErrorMessage(e, "Erro ao carregar a avaliação."));
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load]);

  const persistAnswer = useCallback(async (questionId: string, next: LocalAnswer) => {
    if (isPreview) return;   // prévia não tem aplicação: não há o que gravar
    // Serializa por item: duas gravações simultâneas da mesma resposta colidiam no
    // índice único (uma inseria, a outra falhava com 500)
    const queued = (writeQueue.current[questionId] ?? Promise.resolve()).then(async () => {
    setSavingCount(c => c + 1);
    try {
      await patientAssessmentsService.saveAnswer(applicationId, questionId, {
        optionIds: next.optionIds,
        textValue: next.textValue.trim() === "" ? null : next.textValue,
        comment: next.comment.trim() === "" ? null : next.comment,
      });
      const empty = next.optionIds.length === 0 && next.textValue.trim() === "" && next.comment.trim() === "";
      setAnswerTrail(prev => {
        const copy = { ...prev };
        if (empty) {
          delete copy[questionId];
        } else {
          copy[questionId] = { at: localIsoNow(), by: "você" };
        }
        return copy;
      });
    } catch (e) {
      showToast(patientAssessmentErrorMessage(e, "Erro ao salvar a resposta."), "error");
    } finally {
      setSavingCount(c => c - 1);
    }
    });
    writeQueue.current[questionId] = queued.catch(() => undefined);
    return queued;
  }, [applicationId, isPreview, showToast]);

  /// Grava imediatamente o que estiver esperando o debounce (ao sair do campo, ao fechar a
  /// aba ou ao desmontar) — sem isso, o texto digitado nos últimos 800ms se perdia.
  const flushPending = useCallback((questionId?: string): Promise<unknown> => {
    const timers = textDebounce.current;
    const keys = questionId ? [questionId] : Object.keys(timers);
    const writes = keys.map(key => {
      const timer = timers[key];
      if (!timer) return null;
      clearTimeout(timer);
      delete timers[key];
      const pending = pendingAnswers.current[key];
      return pending ? persistAnswer(key, pending) : null;
    });
    return Promise.all(writes.filter(Boolean));
  }, [persistAnswer]);

  const [savedFeedback, setSavedFeedback] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [resultPreview, setResultPreview] = useState<FillResult[] | null>(null);
  const [loadingResult, setLoadingResult] = useState(false);
  // Modo "uma seção por vez" (RN-536f): índice da seção mãe visível
  const [sectionIndex, setSectionIndex] = useState(0);

  /// Botão explícito de salvar: o autosave já grava, mas ver "salvo" dá a confirmação que a
  /// tela sozinha não dá (pedido do Hilton, 31/08).
  const saveAll = async () => {
    await flushPending();
    setSavedFeedback(true);
    showToast("Respostas salvas.", "success");
    setTimeout(() => setSavedFeedback(false), 2500);
  };

  // Chrome e Safari suspendem timers de aba em segundo plano: ao esconder a aba, grava o
  // pendente na hora. E avisa antes de fechar se ainda houver algo em voo.
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === "hidden") flushPending(); };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const hasPending = Object.keys(textDebounce.current).length > 0 || savingCount > 0;
      if (!hasPending) return;
      flushPending();
      e.preventDefault();
      e.returnValue = "";
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [flushPending, savingCount]);

  const updateAnswer = (questionId: string, patch: Partial<LocalAnswer>, save: "now" | "debounce") => {
    setAnswers(prev => {
      const current = prev[questionId] ?? { optionIds: [], textValue: "", comment: "" };
      const next = { ...current, ...patch };
      if (save === "now") {
        persistAnswer(questionId, next);
      } else {
        const timers = textDebounce.current;
        if (timers[questionId]) clearTimeout(timers[questionId]);
        pendingAnswers.current[questionId] = next;
        timers[questionId] = setTimeout(() => {
          delete timers[questionId];
          persistAnswer(questionId, next);
        }, 800);
      }
      return { ...prev, [questionId]: next };
    });
  };

  /// Régua ÚNICA de completude, igual à do servidor na conclusão: responder não basta se o
  /// item exige comentário ou um mínimo de escolhas. Sem isso o índice pintava verde e o botão
  /// liberava, para a conclusão ser recusada depois.
  const itemState = useCallback((question: FillQuestion) => {
    const a = answers[question.id];
    const marked = a?.optionIds.length ?? 0;
    const hasAnswer = !!a && (marked > 0 || a.textValue.trim() !== "");
    if (!hasAnswer) {
      return { answered: false, complete: false, reason: "" };
    }

    if (question.allowComment && question.commentRequired && !(a!.comment.trim())) {
      return { answered: true, complete: false, reason: "comentário obrigatório" };
    }

    if (question.minChoices !== null && marked > 0 && marked < question.minChoices) {
      return { answered: true, complete: false, reason: `mínimo de ${question.minChoices} opções` };
    }

    return { answered: true, complete: true, reason: "" };
  }, [answers]);

  const answeredCount = useMemo(
    () => (fill ? fill.questions.filter(q => itemState(q).complete).length : 0),
    [fill, itemState],
  );

  /// Pendências que impedem concluir: obrigatório sem resposta, e qualquer item respondido
  /// que ainda não está completo (o servidor recusa os dois casos)
  const missingRequired = useMemo(() => {
    if (!fill) return 0;
    return fill.questions.filter(q => {
      const state = itemState(q);
      if (!state.answered) return q.required;
      return !state.complete;
    }).length;
  }, [fill, itemState]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await patientAssessmentsService.complete(applicationId, interpretation.trim() === "" ? null : interpretation);
      setConfirmComplete(false);
      showToast("Avaliação concluída.", "success");
      await load();
    } catch (e) {
      setConfirmComplete(false);
      showToast(patientAssessmentErrorMessage(e, "Erro ao concluir a avaliação."), "error");
    } finally {
      setCompleting(false);
    }
  };

  const [cancelingReopen, setCancelingReopen] = useState(false);

  /// Reabriu por engano e nada mudou: devolve ao estado concluído sem recalcular.
  const handleCancelReopen = async () => {
    setCancelingReopen(true);
    try {
      await patientAssessmentsService.cancelReopen(applicationId);
      showToast("Reabertura desfeita: a avaliação voltou a ficar concluída.", "success");
      await load();
    } catch (e) {
      showToast(patientAssessmentErrorMessage(e, "Erro ao desfazer a reabertura."), "error");
    } finally {
      setCancelingReopen(false);
    }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await patientAssessmentsService.reopen(applicationId);
      setConfirmReopen(false);
      showToast("Avaliação reaberta para edição.", "success");
      await load();
    } catch (e) {
      setConfirmReopen(false);
      showToast(patientAssessmentErrorMessage(e, "Erro ao reabrir a avaliação."), "error");
    } finally {
      setReopening(false);
    }
  };

  const canEdit = fill?.canEdit ?? false;

  const renderQuestion = (question: FillQuestion, index: number) => {
    const answer = answers[question.id] ?? { optionIds: [], textValue: "", comment: "" };
    const state = itemState(question);
    const incomplete = canEdit && state.answered && !state.complete;   // respondido com pendência
    const awaiting = canEdit && !state.answered && question.required;  // ainda falta responder
    const pending = incomplete || awaiting;
    const pendingColor = incomplete ? colors.error : "#ed6c02";
    const trail = answerTrail[question.id];
    const isChoice = question.questionType === "single_choice" || question.questionType === "multiple_choice";
    return (
      <Box
        key={question.id}
        id={`question-${question.id}`}
        sx={{
          borderTop: "1px solid #f0f0f0", pt: 2.5, mt: 2.5, pb: 0.5, scrollMarginTop: "110px",
          ...(pending ? { borderLeft: `3px solid ${pendingColor}`, pl: 1.25, ml: -1.25 } : {}),
        }}
      >
        {/* Vermelho = impede concluir (o laranja da tela significa "atenção, mas segue").
            Só marca o que bloqueia: item opcional em branco não é pendência. */}
        <Typography sx={{ fontSize: "0.9rem", fontWeight: 500, color: incomplete ? colors.error : colors.text, mb: 0.75 }}>
          {index}. {question.text}{question.required ? " *" : ""}
          {pending && (
            <Typography component="span" sx={{ fontSize: "0.75rem", fontWeight: 600, color: pendingColor, ml: 0.75 }}>
              — {state.reason || "falta responder"}
            </Typography>
          )}
        </Typography>
        {question.description && (
          <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, mb: 0.5 }}>
            {question.description}
          </Typography>
        )}

        {question.questionType === "single_choice" && (
          <RadioGroup
            value={answer.optionIds[0] ?? ""}
            onChange={e => updateAnswer(question.id, { optionIds: [e.target.value] }, "now")}
          >
            {question.options.map(o => (
              <FormControlLabel
                key={o.id}
                value={o.id}
                control={<Radio size="small" disabled={!canEdit} />}
                label={<Typography sx={{ fontSize: "0.875rem" }}>{o.label}</Typography>}
              />
            ))}
          </RadioGroup>
        )}

        {question.questionType === "multiple_choice" && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {question.options.map(o => {
              const checked = answer.optionIds.includes(o.id);
              const capped = question.maxChoices !== null && answer.optionIds.length >= question.maxChoices;
              return (
                <FormControlLabel
                  key={o.id}
                  control={(
                    <Checkbox
                      size="small"
                      checked={checked}
                      disabled={!canEdit || (!checked && capped)}
                      onChange={e => {
                        const next = e.target.checked
                          ? [...answer.optionIds, o.id]
                          : answer.optionIds.filter(id => id !== o.id);
                        updateAnswer(question.id, { optionIds: next }, "now");
                      }}
                    />
                  )}
                  label={<Typography sx={{ fontSize: "0.875rem" }}>{o.label}</Typography>}
                />
              );
            })}
            {(question.minChoices !== null || question.maxChoices !== null) && (() => {
              const short = question.minChoices !== null
                && answer.optionIds.length > 0
                && answer.optionIds.length < question.minChoices;
              return (
                <Typography sx={{ fontSize: typography.fontSize.xs, mt: 0.5, mb: 0.5, color: short ? colors.error : colors.textSecondary }}>
                  {question.minChoices !== null ? `Mínimo ${question.minChoices}` : ""}
                  {question.minChoices !== null && question.maxChoices !== null ? " — " : ""}
                  {question.maxChoices !== null ? `Máximo ${question.maxChoices}` : ""}
                  {short ? ` · faltam ${question.minChoices! - answer.optionIds.length}` : ""}
                </Typography>
              );
            })()}
          </Box>
        )}

        {!isChoice && (
          <TextField
            fullWidth
            size="small"
            multiline={question.questionType === "long_text"}
            minRows={question.questionType === "long_text" ? 3 : undefined}
            value={answer.textValue}
            disabled={!canEdit}
            inputProps={{ maxLength: TEXT_MAX }}
            placeholder="Resposta"
            onChange={e => updateAnswer(question.id, { textValue: e.target.value }, "debounce")}
            onBlur={() => flushPending(question.id)}
          />
        )}

        {question.allowComment && (
          <TextField
            fullWidth
            size="small"
            value={answer.comment}
            disabled={!canEdit}
            inputProps={{ maxLength: COMMENT_MAX }}
            label={`Comentário${question.commentRequired ? " *" : ""}`}
            error={question.commentRequired && itemState(question).answered && !answer.comment.trim()}
            helperText={
              question.commentRequired && itemState(question).answered && !answer.comment.trim()
                ? "Comentário obrigatório para este item"
                : ""
            }
            sx={{ mt: 1.5 }}
            onChange={e => updateAnswer(question.id, { comment: e.target.value }, "debounce")}
            onBlur={() => flushPending(question.id)}
          />
        )}

        {trail && (
          <Typography sx={{ fontSize: "0.72rem", color: colors.textSecondary, mt: 1, display: "block" }}>
            Respondida{trail.by ? ` por ${trail.by}` : ""} em {formatIsoDate(trail.at)} {timeOf(trail.at)}
          </Typography>
        )}
      </Box>
    );
  };

  // Estrutura de exibição: itens da raiz, depois seções mães (com as filhas dentro)
  const structure = useMemo(() => {
    if (!fill) return { root: [] as FillQuestion[], parents: [] as { section: FillSection; questions: FillQuestion[]; children: { section: FillSection; questions: FillQuestion[] }[] }[] };
    const byId = new Map(fill.sections.map(s => [s.id, s]));
    const questionsOf = (sectionId: string | null) =>
      fill.questions.filter(q => q.sectionId === sectionId);
    const parents = fill.sections
      .filter(s => s.parentSectionId === null)
      .map(section => ({
        section,
        questions: questionsOf(section.id),
        children: fill.sections
          .filter(c => c.parentSectionId === section.id)
          .map(child => ({ section: child, questions: questionsOf(child.id) })),
      }));
    // seção filha órfã (mãe fora da lista) não deve sumir
    const orphanChildren = fill.sections.filter(
      s => s.parentSectionId !== null && !byId.get(s.parentSectionId!),
    );
    orphanChildren.forEach(section =>
      parents.push({ section, questions: questionsOf(section.id), children: [] }));
    return { root: questionsOf(null), parents };
  }, [fill]);

  // Índice na MESMA ordem da numeração da tela: raiz, depois cada seção mãe com suas filhas
  const FINISH_STEP = "finish";
  /// Concluir exige os obrigatórios respondidos — a menos que o formulário permita conclusão
  /// parcial, caso em que barrar a página contradiria a própria configuração dele.
  const finishReachable = missingRequired === 0 || (fill?.allowPartialCompletion ?? false);
  const canFinish = (fill?.canEdit ?? false) && !isPreview;

  const navGroups = useMemo<NavGroup[]>(() => {
    if (!fill) return [];
    let n = 0;
    const stateOf = (question: FillQuestion) => itemState(question);
    const toItems = (questions: FillQuestion[]) => questions.map(q => ({
      id: q.id,
      number: ++n,
      text: q.text,
      answered: stateOf(q).complete,
      required: q.required,
      // respondido mas com pendência (comentário/mínimo): é o único caso de vermelho
      incomplete: stateOf(q).answered && !stateOf(q).complete,
    }));

    const counted = (items: NavGroup["questions"]) => ({
      answeredTotal: items.filter(q => q.answered).length,
      questionsTotal: items.length,
    });

    const groups: NavGroup[] = [];
    if (structure.root.length > 0) {
      const items = toItems(structure.root);
      groups.push({ id: "root", title: "Itens iniciais", level: 1, questions: items, ...counted(items) });
    }
    // Mãe ÚNICA que só agrupa repetiria o total do topo do índice: nesse caso ela não vira
    // linha (mesmo critério da impressão). Com várias mães, cada uma informa o seu recorte.
    const onlyGrouper = structure.parents.length === 1
      && structure.parents[0].questions.length === 0
      && structure.root.length === 0;

    structure.parents.forEach(parent => {
      // A numeração segue a tela: mãe primeiro, depois cada filha
      const own = toItems(parent.questions);
      const childGroups = parent.children.map(child => {
        const items = toItems(child.questions);
        return { id: child.section.id, title: child.section.title, level: onlyGrouper ? 1 : 2, questions: items, ...counted(items) };
      });
      // Mãe agrega as filhas (mesmo rollup do cálculo): sem isso ela mostraria "0 de 0"
      const all = [...own, ...childGroups.flatMap(c => c.questions)];
      if (!onlyGrouper) {
        groups.push({ id: parent.section.id, title: parent.section.title, level: 1, questions: own, ...counted(all) });
      }

      groups.push(...childGroups);
    });

    // Conclusão fecha o índice: mostra o estado e leva direto ao resultado + parecer
    if (canFinish) {
      groups.push({
        id: FINISH_STEP,
        title: "Concluir avaliação",
        level: 1,
        questions: [],
        answeredTotal: finishReachable ? 1 : 0,
        questionsTotal: 1,
        hint: finishReachable
          ? "Resultado e parecer"
          : missingRequired === 1 ? "Falta 1 item" : `Faltam ${missingRequired} itens`,
        disabled: !finishReachable,
      });
    }
    return groups;
  }, [fill, structure, itemState, canFinish, finishReachable, missingRequired]);

  /// Um passo do modo paginado = uma SEÇÃO COM PERGUNTAS, seja mãe ou filha ("categoria",
  /// no vocabulário do legado). Paginar só por seção mãe não navegava nada em formulário
  /// cujos itens vivem todas nas subseções — o caso comum aqui.
  const steps = useMemo(() => {
    const list: { id: string; title: string; parentTitle?: string; questions: FillQuestion[] }[] = [];
    if (structure.root.length > 0) {
      list.push({ id: "root", title: "Itens iniciais", questions: structure.root });
    }
    structure.parents.forEach(parent => {
      if (parent.questions.length > 0) {
        list.push({ id: parent.section.id, title: parent.section.title, questions: parent.questions });
      }
      parent.children.forEach(child => {
        if (child.questions.length > 0) {
          list.push({
            id: child.section.id,
            title: child.section.title,
            parentTitle: parent.section.title,
            questions: child.questions,
          });
        }
      });
    });
    return list;
  }, [structure]);

  const bySection = fill?.paginationMode === "section" && steps.length > 1;

  /// No modo paginado, concluir é a ÚLTIMA página (pedido do Hilton): quem está respondendo
  /// não esbarra no parecer e no botão a cada seção.
  const pagedSteps = useMemo(
    () => (bySection && canFinish
      ? [...steps, { id: FINISH_STEP, title: "Concluir avaliação", questions: [] as FillQuestion[] }]
      : steps),
    [bySection, canFinish, steps],
  );

  const currentIndex = Math.min(sectionIndex, Math.max(0, pagedSteps.length - 1));
  const currentStep = bySection ? pagedSteps[currentIndex] : null;
  const onFinishStep = currentStep?.id === FINISH_STEP;

  /// Numeração continua global: soma o que ficou nos passos anteriores
  const questionsBefore = useMemo(
    () => (bySection ? pagedSteps.slice(0, currentIndex).reduce((n, step) => n + step.questions.length, 0) : 0),
    [bySection, pagedSteps, currentIndex],
  );

  /// Trocar de seção leva ao topo da SEÇÃO, não da página — subir até o cabeçalho da
  /// avaliação a cada passo obrigava a rolar de volta. O primeiro render não rola.
  const firstStepRender = useRef(true);
  useEffect(() => {
    if (!bySection || !currentStep) return;
    if (firstStepRender.current) { firstStepRender.current = false; return; }
    document.getElementById(`section-${currentStep.id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [bySection, currentStep]);

  /// A última página mostra o resultado que o instrumento produziu com as respostas atuais —
  /// é com ele em mãos que o profissional escreve o parecer (RN-537h). Recalcula a cada
  /// entrada na página, porque as respostas podem ter mudado.
  useEffect(() => {
    if (!onFinishStep || isPreview || !applicationId) return;
    let cancelled = false;
    setLoadingResult(true);
    patientAssessmentsService.getResultPreview(applicationId)
      .then(preview => { if (!cancelled) setResultPreview(preview.results); })
      .catch(() => { if (!cancelled) setResultPreview(null); })
      .finally(() => { if (!cancelled) setLoadingResult(false); });
    return () => { cancelled = true; };
  }, [onFinishStep, isPreview, applicationId, answeredCount]);

  useEffect(() => {
    if (onFinishStep && !finishReachable) setSectionIndex(i => Math.max(0, i - 1));
  }, [onFinishStep, finishReachable]);

  // Ir para umo item pelo índice precisa abrir o passo dela antes
  const goToQuestion = useCallback((groupId: string) => {
    if (!bySection) return;
    const target = pagedSteps.findIndex(step => step.id === groupId);
    if (target >= 0 && (pagedSteps[target].id !== FINISH_STEP || finishReachable)) {
      setSectionIndex(target);
    }
  }, [bySection, pagedSteps, finishReachable]);

  let questionNumber = 0;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f5f5f5" }}>
      {/* Sem menu nem ícones (pedido do Hilton, 31/08): no meio de uma avaliação, um clique
          fora custa o contexto. A saída é pelo Voltar/Fechar do topo da tela. */}
      <HeaderInternal
        hideNavigation
        rightAction={
          <>
            {!isPreview && fill && (
              <>
                <Tooltip title="Exportar para Excel (respostas e resultados)">
                  <IconButton
                    onClick={() => patientAssessmentsService.exportXlsx(
                      applicationId,
                      `avaliacao_${fill.formTitle.replace(/\s+/g, "_")}.xlsx`,
                    )}
                    sx={{ color: colors.primary }}
                  >
                    <TableChart />
                  </IconButton>
                </Tooltip>
                {fill.status === "completed" && (
                  /* Passado o prazo o botão fica visível e desabilitado: sumir deixaria a
                     impressão de defeito em quem reabriu na semana anterior (RN-537p) */
                  <Tooltip
                    title={fill.canReopen
                      ? `Reabrir para edição (editável até ${fill.reopenDeadline ? formatIsoDate(fill.reopenDeadline) : ""})`
                      : `Prazo de reabertura encerrado${fill.reopenDeadline ? ` em ${formatIsoDate(fill.reopenDeadline)}` : ""} — a avaliação é definitiva`}
                  >
                    <span>
                      <IconButton
                        disabled={!fill.canReopen}
                        onClick={() => setConfirmReopen(true)}
                        sx={{ color: colors.primary }}
                      >
                        <LockOpen />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
                <Tooltip title="Imprimir a avaliação com os itens e respostas">
                  <IconButton
                    onClick={() => window.open(
                      `${window.location.origin}/?page=assessment-print&applicationId=${applicationId}`,
                      "_blank",
                    )}
                    sx={{ color: colors.primary }}
                  >
                    <Print />
                  </IconButton>
                </Tooltip>
              </>
            )}
          <Button
            onClick={() => {
              if (openedInNewTab) {
                window.close();
                return;
              }
              navigateTo(isPreview ? "assessment-forms" : "patient-register",
                isPreview || !fill ? undefined : { id: fill.patientId });
            }}
            startIcon={openedInNewTab ? <Close /> : <ArrowBack />}
            sx={{ textTransform: "none", color: colors.primary }}
          >
            {openedInNewTab ? "Fechar" : "Voltar"}
          </Button>
          </>
        }
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => {}}
      />
      {/* marginTop compensa o header fixo — sem ele o topo do conteúdo fica coberto */}
      <Box component="main" sx={{ padding: "1rem", minHeight: "calc(100vh - 120px)", background: colors.background, marginTop: "85px", flex: 1 }}>
      <Container maxWidth={false} disableGutters>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : error || !fill ? (
          <Paper sx={{ p: 3, borderRadius: "12px" }}>
            <Typography sx={{ color: colors.error }}>{error || "Avaliação não encontrada."}</Typography>
          </Paper>
        ) : (
          <>
            <Paper sx={{ p: 2, borderRadius: "12px", mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>
                  <Typography variant="h2" sx={{ fontSize: "1.15rem", fontWeight: 600, color: colors.text }}>
                    {fill.formTitle}
                  </Typography>
                  <Chip label={`v${fill.formVersion}`} size="small" sx={{ height: 20, fontSize: "0.7rem" }} />
                  <Tooltip title="Índice dos itens">
                    <Button
                      onClick={() => setNavOpen(true)}
                      startIcon={<ListAlt />}
                      sx={{ display: { xs: "inline-flex", md: "none" }, textTransform: "none", color: colors.primary, minWidth: 0 }}
                    >
                      Índice
                    </Button>
                  </Tooltip>
                  <Chip
                    label={isPreview ? "Prévia" : fill.status === "completed" ? "Concluída" : "Em andamento"}
                    size="small"
                    sx={{ height: 22, fontSize: "0.72rem", color: "#fff", backgroundColor: isPreview ? "#6c757d" : fill.status === "completed" ? "#28a745" : "#ff9800" }}
                  />
                </Box>
                <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary, display: { xs: "none", sm: "block" } }}>
                  {isPreview
                    ? "Prévia — nada é gravado"
                    : savingCount > 0 ? "Salvando..." : canEdit ? "Alterações salvas automaticamente" : ""}
                </Typography>
              </Box>
              {fill.isReopened && !isPreview && (
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", backgroundColor: "#e6f7f9", border: "1px solid #b8e6ec", borderRadius: "8px", p: 1, mt: 1, flexWrap: "wrap" }}>
                  <InfoOutlined sx={{ fontSize: 18, color: colors.primary }} />
                  <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary, flex: "1 1 240px" }}>
                    Avaliação <strong>reaberta</strong> para edição. O resultado da conclusão anterior
                    continua registrado até você concluir de novo.
                  </Typography>
                  {!fill.resultOutdated && (
                    <Button
                      size="small"
                      disabled={cancelingReopen}
                      onClick={handleCancelReopen}
                      sx={{ textTransform: "none", color: colors.primary }}
                    >
                      Desfazer reabertura
                    </Button>
                  )}
                </Box>
              )}

              {isPreview ? (
                <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.5 }}>
                  Pré-visualização do formulário — os campos funcionam para você conferir a
                  experiência, mas <strong>nada é gravado</strong>.
                </Typography>
              ) : (
              <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mt: 0.5 }}>
                Paciente: <strong>{fill.patientName}</strong>
                {" — "}{answeredCount}/{fill.questions.length} respondidos
                {fill.status === "completed" && (fill.canReopen && fill.reopenDeadline
                  ? ` — editável até ${formatIsoDate(fill.reopenDeadline)}`
                  : fill.reopenDeadline
                    ? ` — definitiva (prazo de reabertura encerrado em ${formatIsoDate(fill.reopenDeadline)})`
                    : " — definitiva")}
              </Typography>
              )}

              {/* Trilha da avaliação (pedido do Hilton, 31/08): solicitação, início e conclusão */}
              {!isPreview && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 0.5, sm: 2 }, mt: 1, flexDirection: { xs: "column", sm: "row" } }}>
                  <Milestone label="Solicitada" at={fill.requestedAt} by={fill.requestedByName} />
                  <Milestone label="Iniciada" at={fill.startedAt} by={fill.startedByName} pending="não iniciada" />
                  <Milestone label="Finalizada" at={fill.completedAt} by={fill.completedByName} pending="em andamento" />
                </Box>
              )}
              {fill.formDescription && (
                <Typography sx={{ fontSize: "0.85rem", color: colors.text, mt: 1, whiteSpace: "pre-wrap" }}>
                  {fill.formDescription}
                </Typography>
              )}
            </Paper>

            {(fill.status === "completed" || fill.isReopened) && fill.results.length > 0 && (
              <Paper sx={{ p: 2, borderRadius: "12px", mb: 2 }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: colors.text, mb: 1 }}>
                  {fill.isReopened ? "Resultado da conclusão anterior" : "Resultados"}
                </Typography>
                {fill.results.length === 0 ? (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>
                    Este formulário não tem cálculo de resultado configurado.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                    {fill.results.map(r => (
                      <ResultCard key={`${r.sectionId ?? "geral"}-${r.calcTypeCode}`} result={r} />
                    ))}
                  </Box>
                )}
                {fill.interpretationGuide && <GuideBox text={fill.interpretationGuide} />}
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.text, mb: 0.5 }}>
                    Parecer do profissional
                  </Typography>
                  <Typography sx={{ fontSize: "0.875rem", color: colors.text, whiteSpace: "pre-wrap" }}>
                    {fill.professionalInterpretation || "(sem parecer registrado)"}
                  </Typography>
                </Box>
                {fill.resultOutdated && (
                  <Box className="c4u-info-box c4u-info-box--warning">
                    <InfoOutlined className="c4u-info-box__icon" sx={{ fontSize: "1.1rem" }} />
                    <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textPrimary }}>
                      As respostas mudaram depois deste cálculo. <strong>Conclua novamente</strong> para
                      o resultado corresponder ao que está respondido hoje.
                    </Typography>
                  </Box>
                )}

              </Paper>
            )}

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
              {/* Índice: fica fixo enquanto a página rola; no celular vira gaveta */}
              {navGroups.length > 0 && (
                <Paper sx={{ display: { xs: "none", md: "block" }, width: { md: 300, xl: 340 }, flexShrink: 0, position: "sticky", top: "100px", maxHeight: "calc(100vh - 140px)", overflowY: "auto", p: 1.5, borderRadius: "12px" }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: colors.text, mb: 1 }}>
                    Índice · {answeredCount}/{fill.questions.length} respondidos
                  </Typography>
                  <QuestionNav groups={navGroups} onSelect={goToQuestion} />
                </Paper>
              )}

              <Box sx={{ flex: 1, minWidth: 0 }}>
            {bySection && !onFinishStep ? (
              <Paper id={`section-${currentStep!.id}`} sx={{ p: 2, borderRadius: "12px", mb: 2, scrollMarginTop: "110px" }}>
                {currentStep!.parentTitle && (
                  <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                    {currentStep!.parentTitle}
                  </Typography>
                )}
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: colors.primary }}>
                  {currentStep!.title}
                </Typography>
                {currentStep!.questions.map((q, i) => renderQuestion(q, questionsBefore + i + 1))}
              </Paper>
            ) : bySection ? null : (
              <>
                {structure.root.length > 0 && (
                  <Paper id="section-root" sx={{ p: 2, borderRadius: "12px", mb: 2, scrollMarginTop: "110px" }}>
                    {structure.root.map(q => renderQuestion(q, ++questionNumber))}
                  </Paper>
                )}

                {structure.parents.map(parent => (
                  <Paper key={parent.section.id} id={`section-${parent.section.id}`} sx={{ p: 2, borderRadius: "12px", mb: 2, scrollMarginTop: "110px" }}>
                    <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: colors.primary }}>
                      {parent.section.title}
                    </Typography>
                    {parent.section.description && (
                      <Typography sx={{ fontSize: "0.82rem", color: colors.textSecondary }}>
                        {parent.section.description}
                      </Typography>
                    )}
                    {fill.status === "completed" && parent.section.interpretationGuide && (
                      <GuideBox text={parent.section.interpretationGuide} />
                    )}
                    {parent.questions.map(q => renderQuestion(q, ++questionNumber))}
                    {parent.children.map(child => (
                      <Box key={child.section.id} id={`section-${child.section.id}`} sx={{ mt: 2, pl: { xs: 0, sm: 1.5 }, borderLeft: { xs: "none", sm: `3px solid ${colors.primary}22` }, scrollMarginTop: "110px" }}>
                        <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.text }}>
                          {child.section.title}
                        </Typography>
                        {child.section.description && (
                          <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
                            {child.section.description}
                          </Typography>
                        )}
                        {fill.status === "completed" && child.section.interpretationGuide && (
                          <GuideBox text={child.section.interpretationGuide} />
                        )}
                        {child.questions.map(q => renderQuestion(q, ++questionNumber))}
                      </Box>
                    ))}
                  </Paper>
                ))}
              </>
            )}

            {canEdit && !isPreview && (!bySection || onFinishStep) && (
              <Paper id={`section-${FINISH_STEP}`} sx={{ p: 2, borderRadius: "12px", mb: 2, scrollMarginTop: "110px" }}>
                <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: colors.text, mb: 1 }}>
                  Concluir avaliação
                </Typography>
                {bySection && (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mb: 1.5 }}>
                    {answeredCount} de {fill.questions.length} itens respondidos.
                    {missingRequired > 0
                      ? " Use o índice ao lado para voltar às que faltam."
                      : " Tudo respondido."}
                  </Typography>
                )}

                {/* Resultado com as respostas atuais: é com ele que o parecer é escrito */}
                {loadingResult ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <CircularProgress size={16} sx={{ color: colors.primary }} />
                    <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>
                      Calculando o resultado...
                    </Typography>
                  </Box>
                ) : resultPreview && resultPreview.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.text, mb: 1 }}>
                      Resultado do instrumento
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
                      {resultPreview.map(r => (
                        <ResultCard key={`${r.sectionId ?? "geral"}-${r.calcTypeCode}`} result={r} />
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary, mt: 0.75 }}>
                      Calculado com as respostas atuais; é este valor que fica registrado ao concluir.
                    </Typography>
                    {fill.interpretationGuide && <GuideBox text={fill.interpretationGuide} />}
                  </Box>
                ) : resultPreview !== null && (
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, mb: 2 }}>
                    Este formulário não tem cálculo de resultado configurado — a conclusão fica só
                    com o seu parecer.
                  </Typography>
                )}
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size="small"
                  label="Parecer do profissional"
                  value={interpretation}
                  inputProps={{ maxLength: PARECER_MAX }}
                  onChange={e => setInterpretation(e.target.value)}
                  helperText="O instrumento informa; a conclusão clínica é sua. O parecer fica registrado com o resultado."
                />
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1.5, flexWrap: "wrap" }}>
                  {!bySection && (
                    <Button
                      variant="outlined"
                      disabled={savingCount > 0}
                      onClick={saveAll}
                      startIcon={savedFeedback ? <Check /> : <Save />}
                      sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                    >
                      {savingCount > 0 ? "Salvando..." : savedFeedback ? "Salvo" : "Salvar respostas"}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    disabled={completing || (missingRequired > 0 && !fill.allowPartialCompletion)}
                    onClick={() => setConfirmComplete(true)}
                    sx={{ textTransform: "none", backgroundColor: colors.primary, "&:hover": { backgroundColor: "#029AAB" } }}
                  >
                    Concluir avaliação
                  </Button>
                  {missingRequired > 0 && (
                    <Typography sx={{ fontSize: "0.8rem", color: fill.allowPartialCompletion ? colors.textSecondary : colors.error }}>
                      {missingRequired === 1 ? "1 item pendente" : `${missingRequired} itens pendentes`}
                      {fill.allowPartialCompletion ? " (conclusão parcial permitida)" : ""}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
              {bySection && (
                <Paper sx={{ p: 1.5, borderRadius: "12px", mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    disabled={currentIndex <= 0}
                    onClick={() => setSectionIndex(i => Math.max(0, i - 1))}
                    sx={{ textTransform: "none", color: colors.primary }}
                  >
                    Seção anterior
                  </Button>
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textSecondary, textAlign: "center", flex: "1 1 auto" }}>
                    {currentStep?.title} · {currentIndex + 1} de {pagedSteps.length}
                  </Typography>
                  {!onFinishStep && canEdit && (
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={savingCount > 0}
                      onClick={saveAll}
                      startIcon={savedFeedback ? <Check /> : <Save />}
                      sx={{ textTransform: "none", color: colors.primary, borderColor: colors.primary }}
                    >
                      {savingCount > 0 ? "Salvando..." : savedFeedback ? "Salvo" : "Salvar respostas"}
                    </Button>
                  )}
                  <Tooltip
                    title={
                      !finishReachable && pagedSteps[currentIndex + 1]?.id === FINISH_STEP
                        ? `Responda ${missingRequired === 1 ? "o item pendente" : `os ${missingRequired} itens pendentes`} para concluir`
                        : ""
                    }
                  >
                    <span>
                      <Button
                        disabled={
                          currentIndex >= pagedSteps.length - 1
                          || (pagedSteps[currentIndex + 1]?.id === FINISH_STEP && !finishReachable)
                        }
                        onClick={() => setSectionIndex(i => Math.min(pagedSteps.length - 1, i + 1))}
                        sx={{ textTransform: "none", color: colors.primary }}
                      >
                        {pagedSteps[currentIndex + 1]?.id === FINISH_STEP ? "Concluir avaliação" : "Próxima seção"}
                      </Button>
                    </span>
                  </Tooltip>
                </Paper>
              )}
              </Box>
            </Box>
          </>
        )}
      </Container>
      </Box>
      <Drawer anchor="left" open={navOpen} onClose={() => setNavOpen(false)} PaperProps={{ sx: { width: 300, p: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: colors.text }}>
            Índice{fill ? ` · ${answeredCount}/${fill.questions.length}` : ""}
          </Typography>
          <IconButton size="small" onClick={() => setNavOpen(false)}><Close /></IconButton>
        </Box>
        <QuestionNav groups={navGroups} onSelect={goToQuestion} onNavigate={() => setNavOpen(false)} />
      </Drawer>

      <FooterInternal className="login-footer-component" />

      <ConfirmModal
        isOpen={confirmComplete}
        title="Concluir avaliação"
        message="Ao concluir, os resultados são calculados e registrados. A avaliação poderá ser reaberta em até 15 dias; depois se torna definitiva."
        type="info"
        confirmButtonText="Concluir"
        cancelButtonText="Cancelar"
        isProcessing={completing}
        onConfirm={handleComplete}
        onCancel={() => { if (!completing) setConfirmComplete(false); }}
      />

      <ConfirmModal
        isOpen={confirmReopen}
        title="Reabrir avaliação"
        message="A avaliação volta para Em andamento. O resultado registrado continua valendo até você concluir de novo."
        type="info"
        confirmButtonText="Reabrir"
        cancelButtonText="Cancelar"
        isProcessing={reopening}
        onConfirm={handleReopen}
        onCancel={() => { if (!reopening) setConfirmReopen(false); }}
      />

      <SavingOverlay open={completing || reopening} message={completing ? "Concluindo avaliação..." : "Reabrindo avaliação..."} />
      <Toast isVisible={toast.isVisible} message={toast.message} type={toast.type} onClose={hideToast} />
    </Box>
  );
};

export default AssessmentApplication;
